/**
 * SessionHistorySidebar Component
 *
 * Displays chat session history with grouped conversations.
 * Supports both desktop sidebar and mobile drawer modes.
 *
 * Extracted from AgentChat.tsx to eliminate ~120 lines of duplication.
 */

import type { ChatSession } from '../../types/agents'
import { SessionListItem } from './SessionListItem'
import { Icon } from '@/design-system/atoms/Icon'

interface SessionGroup {
  date: string
  displayDate: string
  sessions: ChatSession[]
}

interface SessionHistorySidebarProps {
  /* Data */
  sessions: ChatSession[]
  groupedSessions: SessionGroup[]
  currentSessionId: string | null
  hasMessages: boolean

  /* Handlers */
  onNewChat: () => void
  onSelectSession: (sessionId: string) => void
  onShareSession?: (session: ChatSession) => void
  formatTime: (timestamp: string) => string

  /* Toggle between History/Metadata */
  onToggleMode?: () => void
  toggleButtonText?: string

  /* Mobile-specific */
  isMobile?: boolean
  isOpen?: boolean
  onClose?: () => void
}

export const SessionHistorySidebar: React.FC<SessionHistorySidebarProps> = ({
  sessions,
  groupedSessions,
  currentSessionId,
  hasMessages,
  onNewChat,
  onSelectSession,
  onShareSession,
  formatTime,
  onToggleMode,
  toggleButtonText,
  isMobile = false,
  isOpen = true,
  onClose,
}) => {
  const handleNewChat = () => {
    onNewChat()
    if (isMobile && onClose) {
      onClose()
    }
  }

  const handleSelectSession = (sessionId: string) => {
    onSelectSession(sessionId)
    if (isMobile && onClose) {
      onClose()
    }
  }

  const content = (
    <>
      {/* Header */}
      <div
        data-testid="session-history-header"
        className="flex-shrink-0 p-3 bg-white/40 dark:bg-gray-800/40 backdrop-blur-md border-b border-gray-200/50 dark:border-gray-700/50"
      >
        <div className="flex items-center justify-between mb-2.5">
          {/* Título - lado esquerdo */}
          <div className="flex items-center gap-1.5">
            <div className="w-0.5 h-5 bg-gradient-to-b from-brand-500 to-brand-600 rounded-full"></div>
            <h3
              data-testid="session-history-title"
              className="text-sm font-bold text-gray-900 dark:text-white tracking-tight"
            >
              Histórico
            </h3>
          </div>

          <div className="flex items-center gap-1">
            {/* Toggle button - lado direito */}
            {onToggleMode && toggleButtonText && (
              <button
                onClick={onToggleMode}
                className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all"
                aria-label={toggleButtonText}
                title={toggleButtonText}
              >
                <Icon name="list" size={16} />
              </button>
            )}

            {/* Close button for mobile drawer */}
            {isMobile && onClose && (
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-all"
                aria-label="Fechar histórico"
              >
                <Icon name="x" size={16} />
              </button>
            )}
          </div>
        </div>

        {/* Controls - always visible on desktop, visible when drawer open on mobile */}
        <div className="space-y-2">
          {/* New Chat button - ghost variant para não dominar a sidebar */}
          {hasMessages && (
            <button
              onClick={handleNewChat}
              className={`w-full flex items-center justify-center gap-2 px-3 py-2 bg-surface-elevated hover:bg-brand-50 dark:hover:bg-brand-950/40 text-brand-600 dark:text-brand-400 border border-brand-200 dark:border-brand-900/50 hover:border-brand-300 dark:hover:border-brand-800 rounded-lg transition-colors duration-150 text-sm font-semibold ${isMobile ? 'mb-2' : ''}`}
            >
              <Icon name="plus" size={16} />
              Nova Conversa
            </button>
          )}

          {/* Search input */}
          <div className="relative">
            <input
              type="text"
              placeholder="Buscar..."
              className="w-full pl-8 pr-3 py-1.5 bg-white/60 dark:bg-gray-800/60 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-md text-xs placeholder:text-gray-400 dark:placeholder:text-gray-500 focus:outline-none focus:ring-2 focus:ring-brand-500/20 focus:border-brand-500/50 dark:text-white transition-all"
            />
            <Icon
              name="search"
              size={14}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
            />
          </div>
        </div>
      </div>

      {/* Sessions list - independent scroll */}
      <div className="flex-1 overflow-y-auto px-3 py-3 custom-scrollbar">
        {sessions.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 px-4">
            <div className="w-16 h-16 bg-gradient-to-br from-gray-100 to-gray-200 dark:from-gray-800 dark:to-gray-700 rounded-2xl flex items-center justify-center mb-4">
              <Icon
                name="chat"
                size={32}
                strokeWidth={1.5}
                className="text-gray-400 dark:text-gray-500"
              />
            </div>
            <p className="text-sm text-gray-500 dark:text-gray-400 text-center">
              Nenhuma conversa anterior
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {groupedSessions.map(group => (
              <div key={group.date}>
                <div className="flex items-center gap-2 mb-1.5 px-1">
                  <h4 className="text-[10px] font-semibold text-content-tertiary uppercase tracking-wider">
                    {group.displayDate}
                  </h4>
                  <div className="flex-1 h-px bg-gray-200 dark:bg-gray-800"></div>
                </div>

                <div className="flex flex-col gap-0.5">
                  {group.sessions.map(session => (
                    <SessionListItem
                      key={session.id}
                      session={session}
                      isActive={currentSessionId === session.id}
                      onSelect={handleSelectSession}
                      onShare={onShareSession}
                      formatTime={formatTime}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  )

  // Desktop: render as static sidebar
  if (!isMobile) {
    return (
      <div
        data-testid="session-history-sidebar-desktop"
        className="w-full lg:border-l border-gray-200/50 dark:border-gray-700/50 flex flex-col order-first lg:order-last flex-shrink-0 bg-surface-secondary dark:bg-gray-800 min-h-0 h-full"
      >
        {content}
      </div>
    )
  }

  // Mobile: render as drawer with overlay
  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-50 lg:hidden"
      data-testid="session-history-sidebar-mobile-overlay"
    >
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div
        data-testid="session-history-sidebar-mobile"
        className="absolute top-0 right-0 w-80 h-full bg-surface-secondary dark:bg-gray-800 shadow-2xl border-l border-gray-200/50 dark:border-gray-700/50 transform transition-transform duration-300 ease-in-out translate-x-0 flex flex-col"
      >
        {content}
      </div>
    </div>
  )
}

export default SessionHistorySidebar
